<script setup lang="ts">
/**
 * Rename one document or folder — shared by the documents sidebar, `/app/docs` (MAR-957) and the
 * case document browser (MAR-948).
 *
 * The sidebar's rename (context menu and F2) used to set `renamingId`/`renameValue`, state that no
 * element rendered, so both entry points silently did nothing. The finder had a working modal; this
 * is that modal, lifted out so there is one rename UI rather than one working and one not.
 *
 * A `ui/dialog` (MAR-948): Escape closes it from anywhere inside, focus is trapped while it is open
 * and handed back afterwards. By default it renames through the documents store; a caller whose
 * rows live elsewhere passes `save` (the case browser does, to re-read its folder path).
 *
 * A failed save stays open with the reason inline, the typed name intact, rather than closing as if
 * it had worked.
 */
import { useDocumentsStore } from '~/stores/documents'
import { apiErrorMessage } from '~/utils/apiErrorMessage'
import { Dialog, DialogContent, DialogTitle } from '~/components/ui/dialog'

const props = defineProps<{
  target: { id: string; name: string } | null
  /** Replaces the documents-store rename. A rejection keeps the dialog open with its message. */
  save?: (id: string, name: string) => Promise<unknown>
}>()
const emit = defineEmits<{ close: [] }>()

const { t } = useI18n()
const { renameItem } = useDocumentsStore()

const name = ref('')
const saving = ref(false)
const error = ref('')
const input = ref<HTMLInputElement | null>(null)

watch(() => props.target, (target) => {
  name.value = target?.name ?? ''
  error.value = ''
}, { immediate: true })

/** Focus lands in the field with the old name selected, so typing replaces it. */
function focusName(e: Event) {
  e.preventDefault()
  input.value?.focus()
  input.value?.select()
}

async function submit() {
  const target = props.target
  const next = name.value.trim()
  if (!target || saving.value) return
  if (!next || next === target.name) { emit('close'); return }
  saving.value = true
  error.value = ''
  try {
    await (props.save ?? renameItem)(target.id, next)
    emit('close')
  } catch (err: any) {
    error.value = apiErrorMessage(err, t('documents.renameFailed'))
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <Dialog :open="!!target" @update:open="(open) => { if (!open) emit('close') }">
    <DialogContent
      class="z-[110] max-w-sm gap-0 rounded-lg bg-card p-4 shadow-xl"
      :show-close-button="false"
      :aria-describedby="undefined"
      data-testid="rename-item-dialog"
      @open-auto-focus="focusName"
    >
      <DialogTitle class="mb-2 text-sm font-medium tracking-normal">{{ t('common.rename') }}</DialogTitle>
      <input
        ref="input"
        v-model="name"
        :aria-label="t('common.rename')"
        class="h-9 w-full rounded-md border border-input bg-background px-2.5 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        @keyup.enter="submit"
      />
      <p v-if="error" role="alert" class="mt-2 text-xs text-destructive">{{ error }}</p>
      <div class="mt-3 flex justify-end gap-2">
        <Button variant="outline" size="sm" @click="emit('close')">{{ t('common.cancel') }}</Button>
        <Button size="sm" :disabled="!name.trim() || saving" @click="submit">{{ t('common.save') }}</Button>
      </div>
    </DialogContent>
  </Dialog>
</template>
